'use client'

import {
  BugReportOutlined,
  CheckCircle,
  ChevronRight,
  DescriptionOutlined,
  MailOutline,
  Search,
  Send
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Divider,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
  styled
} from '@mui/material'
import { useTranslations } from 'next-intl'
import { useCallback, useRef, useState } from 'react'

import getCustomerAssets from '~/auth/assets'
import EmailSupport from '~/components/EmailSupport'

import { useCreateBugReportMutation } from '~/data/api/client'

import { buildBugReportParams } from './buildBugReportParams'

const SYNOP_DOCS_URL = 'https://docs.synop.ai'

export type HelpCategory = 'general' | 'bug' | 'feature' | 'support'

type AssistantHelpPanelProps = {
  organizationId: string | undefined
  pagePath: string
  pageTitle?: string
  pageContext?: string
}

export default function AssistantHelpPanel({
  organizationId,
  pagePath,
  pageTitle,
  pageContext
}: AssistantHelpPanelProps) {
  const t = useTranslations('features.AssistantBar')
  const messageRef = useRef<HTMLTextAreaElement>(null)
  const [category, setCategory] = useState<HelpCategory>('general')
  const [message, setMessage] = useState('')
  const [submitState, setSubmitState] = useState<'idle' | 'success' | 'error'>('idle')
  const [createBugReport, { isLoading: isSubmitting }] = useCreateBugReportMutation()

  const customerAssets = getCustomerAssets()
  const supportEmail = customerAssets?.supportEmail ?? 'support@synop.ai'

  const openDocs = useCallback(() => {
    window.open(SYNOP_DOCS_URL, '_blank', 'noopener,noreferrer')
  }, [])

  const focusMessage = useCallback((nextCategory: HelpCategory) => {
    setCategory(nextCategory)
    setSubmitState('idle')
    setTimeout(() => messageRef.current?.focus(), 60)
  }, [])

  const contactSupport = useCallback(() => {
    window.open(`mailto:${supportEmail}`, '_blank', 'noopener,noreferrer')
  }, [supportEmail])

  const submitMessage = useCallback(async () => {
    if (!organizationId || !message.trim() || isSubmitting) return

    setSubmitState('idle')
    try {
      await createBugReport({
        'X-Organization-Id': organizationId,
        bugReportCreateParams: buildBugReportParams({
          message: message.trim(),
          category,
          pagePath,
          pageTitle,
          pageContext
        })
      }).unwrap()
      setMessage('')
      setSubmitState('success')
    } catch {
      setSubmitState('error')
    }
  }, [category, createBugReport, isSubmitting, message, organizationId, pageContext, pagePath, pageTitle])

  const canSubmit = Boolean(organizationId && message.trim() && !isSubmitting)

  return (
    <HelpBody>
      <TextField
        fullWidth
        size="small"
        disabled
        placeholder={t('helpSearchPlaceholder')}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          )
        }}
        helperText={t('helpSearchComingSoon')}
      />

      <Box>
        <SectionLabel>{t('helpQuickLinks')}</SectionLabel>
        <Stack sx={{ mt: 0.5 }}>
          <QuickLink onClick={openDocs} aria-label={t('openHelp')}>
            <DescriptionOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ flex: 1, textAlign: 'left' }}>
              {t('helpViewApiDocs')}
            </Typography>
            <ChevronRight fontSize="small" sx={{ color: 'text.secondary' }} />
          </QuickLink>
          <QuickLink onClick={() => focusMessage('bug')} aria-label={t('helpReportBug')}>
            <BugReportOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ flex: 1, textAlign: 'left' }}>
              {t('helpReportBug')}
            </Typography>
            <ChevronRight fontSize="small" sx={{ color: 'text.secondary' }} />
          </QuickLink>
          <QuickLink onClick={contactSupport} aria-label={t('helpContactSupport')}>
            <MailOutline fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ flex: 1, textAlign: 'left' }}>
              {t('helpContactSupport')}
            </Typography>
            <ChevronRight fontSize="small" sx={{ color: 'text.secondary' }} />
          </QuickLink>
        </Stack>
      </Box>

      <Divider />

      <Box>
        <SectionLabel>{t('helpSendMessage')}</SectionLabel>
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          <TextField
            select
            fullWidth
            size="small"
            label={t('helpCategoryLabel')}
            value={category}
            onChange={(event) => {
              setCategory(event.target.value as HelpCategory)
              setSubmitState('idle')
            }}
          >
            <MenuItem value="general">{t('helpCategoryGeneral')}</MenuItem>
            <MenuItem value="bug">{t('helpCategoryBug')}</MenuItem>
            <MenuItem value="feature">{t('helpCategoryFeature')}</MenuItem>
            <MenuItem value="support">{t('helpCategorySupport')}</MenuItem>
          </TextField>
          <TextField
            inputRef={messageRef}
            fullWidth
            multiline
            minRows={3}
            placeholder={t('helpMessagePlaceholder')}
            value={message}
            onChange={(event) => {
              setMessage(event.target.value)
              setSubmitState('idle')
            }}
          />
          {submitState === 'success' ? (
            <Alert severity="success" icon={<CheckCircle fontSize="inherit" />}>
              {t('helpSubmitSuccess')}
            </Alert>
          ) : null}
          {submitState === 'error' ? (
            <Alert severity="error">
              {t('helpSubmitError')}
              {' '}
              <EmailSupport />
            </Alert>
          ) : null}
          <Button
            fullWidth
            variant="contained"
            color="success"
            disabled={!canSubmit}
            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <Send fontSize="small" />}
            sx={{ textTransform: 'none' }}
            onClick={() => void submitMessage()}
          >
            {t('helpSendButton')}
          </Button>
        </Stack>
      </Box>
    </HelpBody>
  )
}

const HelpBody = styled(Box)(({ theme }) => ({
  overflowY: 'auto',
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2)
}))

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.caption.fontSize,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary
}))

const QuickLink = styled(ButtonBase)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  width: '100%',
  padding: theme.spacing(1),
  borderRadius: theme.spacing(1),
  color: theme.palette.text.primary,
  '&:hover': { backgroundColor: theme.palette.action.hover },
  '&.Mui-disabled': { color: theme.palette.text.disabled }
}))
